// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";
import Button from "../../../../../components/Button/Button";
import api from "../../../../../helpers/api";
import CompanyReps from "../../../components/CompanyReps/CompanyReps";
import i18n from "@helpers/i18n";
import Settings from "@classes/Settings";
import OptionsList from "../../../components/OptionsList/OptionsList";
import Select from "../../../../../components/Select/Select";
import QuestionField from "../../../components/QuestionField/QuestionField";


export default class CobCustomerInformation extends React.PureComponent {
	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		config: PropTypes.object,
		onChange: PropTypes.func.isRequired,
	};

	static companyExclusive = false;
	static privateExclusive = false;
	static fields = _getFields();

	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState) {
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields) {
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}
	}

	render() {
		const {props, state} = this;
		const {title} = props;
		const classes = createClassName("Cob-CustomerInformation", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled,
		});

		const langCode = Settings.get("LANGUAGE");
		let ssnShort = langCode === "fi" ? state.fields.customer_social_security_number : state.fields.customer_social_security_number.replaceAll("-", "");

		let isCompany = false;
		if (props.config && props.config.company_only) {
			isCompany = true;
		}
		else {
			isCompany = !Validator.validateSsn(ssnShort, true) && Validator.validateOrgNum(ssnShort, true);
			if (ssnShort.length > 10) {
				ssnShort = ssnShort.substring(2, ssnShort.length);
			}
		}

		const isFetchButtonDisabled = isCompany ?
			!Validator.validateOrgNum(state.fields.customer_social_security_number, true) :
			!Validator.validateSsn(state.fields.customer_social_security_number, true);

		/**
		 * Check if the person is a minor. If so, we will need to enter their guardians.
		 */
		let needsGuardianToSign = false;
		if (state.fields.customer_social_security_number.startsWith("20")) {
			const dateOfBirthYear = state.fields.customer_social_security_number.substring(0, 4);
			const dateOfBirthMonth = state.fields.customer_social_security_number.substring(4, 6);
			const dateOfBirthDay = state.fields.customer_social_security_number.substring(6, 8);
			const dateOfBirth = new Date(dateOfBirthYear + "-" + dateOfBirthMonth + "-" + dateOfBirthDay);

			const date18yearsAgo = new Date();
			date18yearsAgo.setFullYear(date18yearsAgo.getFullYear() - 18);

			if (dateOfBirth > date18yearsAgo) {
				needsGuardianToSign = true;
			}
		}

		const today = new Date();
		let month = (today.getMonth() + 1);
		if (month < 10) {
			month = "0" + month;
		}

		let day = today.getDate();
		if (day < 10) {
			day = "0" + day;
		}

		let date = today.getFullYear() + '-' + month + '-' + day;

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<input type="hidden" name="house_comp_name" value="GlofiTech Solutions AB"/>
				<input type="hidden" name="house_address_street" value="Kungsgatan 37"/>
				<input type="hidden" name="house_address_zipcode" value="111 56"/>
				<input type="hidden" name="house_address_city" value="Stockholm"/>
				<input type="hidden" name="house_comp_no" value="559078-3477"/>
				<input type="hidden" name="house_bgno" value="1234-5678"/>

				<div className="Cob-Fields">
					<h2>{title}</h2>

					<FieldsCollection
						fieldsPerRow={2}
						fields={[
							{
								render: (
									<EditableField
										name="customer_social_security_number"
										label="Personnummer"
										placeholder={i18n("customer_info", "ssn_format")}
										value={state.fields.customer_social_security_number}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							},
							{
								render: (
									<div style={{height: "100%", justifyContent: "center"}}>
										<Button label={i18n("customer_info", "fetch_cta")}
												appearance="success"
												disabled={isFetchButtonDisabled}
												busy={state.busy}
												onClick={this._fetchCustomerDetails}/>
									</div>
								)
							}
						]}
					/>

					<p style={{padding: "0 5px"}}>
						{i18n("customer_info", "fill_manually_or")}
						<span className="link"
							  onClick={this._onUnlock}>{i18n("customer_info", "fill_manually_action")}</span>
					</p>

					{isCompany && (
						<FieldsCollection
							fieldsPerRow={2}
							fields={[
								{
									render: (
										<EditableField
											name="customer_firstname"
											label={i18n("customer_info", "business_name")}
											value={state.fields.customer_firstname}
											valid={state.fields.customer_firstname.length > 0}
											onChange={this._onFieldChange}
											onBlur={this._onFieldBlur}
											readOnly={state.fieldsLocked}
											className={state.fields.customer_firstname.trim().length === 0 ? "empty" : ""}
											required
										/>
									),
								}
							]}
						/>
					)}

					{!isCompany && (
						<FieldsCollection
							fieldsPerRow={2}
							fields={[
								{
									render: (
										<EditableField
											name="customer_firstname"
											label={"Förnamn"}
											value={state.fields.customer_firstname}
											valid={state.fields.customer_firstname.length > 0}
											onChange={this._onFieldChange}
											onBlur={this._onFieldBlur}
											readOnly={state.fieldsLocked}
											className={state.fields.customer_firstname.trim().length === 0 ? "empty" : ""}
											required
										/>
									),
								},
								{
									render: (
										<EditableField
											name="customer_lastname"
											label="Efternamn"
											value={state.fields.customer_lastname}
											valid={state.fields.customer_lastname.length > 0}
											onChange={this._onFieldChange}
											onBlur={this._onFieldBlur}
											readOnly={state.fieldsLocked}
											className={state.fields.customer_lastname.trim().length === 0 ? "empty" : ""}
											required
										/>
									)
								}
							]}
						/>
					)}

					<input type="hidden" name="customer_name"
						   value={state.fields.customer_firstname + (isCompany ? "" : (" " + state.fields.customer_lastname))}/>

					<input type="hidden" name="customer_autogiro_custno"
						   value={state.fields.customer_social_security_number}/>

					<input type="hidden" name="customer_social_security_number" value={ssnShort}/>

					<FieldsCollection
						fieldsPerRow={3}
						fields={[
							{
								render: (
									<EditableField
										name="customer_address_street"
										label={i18n("customer_info", "address")}
										value={state.fields.customer_address_street}
										valid={state.fields.customer_address_street.length > 0}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										readOnly={state.fieldsLocked}
										className={state.fields.customer_address_street.trim().length === 0 ? "empty" : ""}
										required
									/>
								),
							},
							{
								render: (
									<EditableField
										name="customer_address_zipcode"
										label={i18n("customer_info", "zipcode")}
										value={state.fields.customer_address_zipcode}
										valid={state.fields.customer_address_zipcode.length > 0}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										readOnly={state.fieldsLocked}
										className={state.fields.customer_address_zipcode.trim().length === 0 ? "empty" : ""}
										required
									/>
								),
							}, {
								render: (
									<EditableField
										name="customer_address_city"
										label={i18n("customer_info", "city")}
										value={state.fields.customer_address_city}
										valid={state.fields.customer_address_city.length > 0}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										readOnly={state.fieldsLocked}
										className={state.fields.customer_address_city.trim().length === 0 ? "empty" : ""}
										required
									/>
								),
							},
						]}
					/>

					<FieldsCollection
						fieldsPerRow={3}
						fields={[
							{
								render: (
									<EditableField
										type="tel"
										name="customer_phonenumber_cell"
										label="Mobiltelefon"
										value={state.fields.customer_phonenumber_cell}
										valid={state.fields.customer_phonenumber_cell.length > 0}
										className={state.fields.customer_phonenumber_cell.trim().length === 0 ? "empty" : ""}
										readOnly={state.fieldsLocked}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							},
							{
								render: (
									<EditableField
										type="tel"
										name="customer_phonenumber_home"
										label="Hemtelefon"
										value={state.fields.customer_phonenumber_home}
										valid={state.validFields.customer_phonenumber_home}
										className={state.fields.customer_phonenumber_home.trim().length === 0 ? "empty" : ""}
										readOnly={state.fieldsLocked}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
									/>
								)
							}
						]}
					/>

					{isCompany && (
						<CompanyReps
							orgNr={state.fields.customer_social_security_number}
							firma={state.fields.company_firma}
							ceo={state.fields.company_ceo}
							board={state.fields.company_board}
							onRepsUpdated={this._onRepsUpdated}/>
					)}

					{!isCompany && (
						<React.Fragment>
							<p style={{padding: "0 5px", marginTop: "30px"}}>
								{i18n("customer_info", "email_info")}
							</p>

							<FieldsCollection
								fieldsPerRow={2}
								fields={[
									{
										render: (
											<EditableField
												type="email"
												name="customer_email"
												label={i18n("customer_info", "email")}
												value={state.fields.customer_email}
												valid={state.validFields.customer_email}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									}
								]}
							/>
						</React.Fragment>
					)}

					{needsGuardianToSign && (
						<div style={{display: "block"}}>
							<h3 style={{
								width: "100%",
								margin: "40px 0 8px 0",
								borderTop: "1px solid #EEE",
								paddingTop: "40px"
							}}>
								Ange vårdnadshavare
							</h3>

							<p>
								Då personnumret du angett tillhör en minderårig måste vårdnadshavarna anges. I det fall
								enbart en vårdnadshavare finns ber vi dig ta kontakt direkt med Fair
								(backoffice@fairinvestments.se).
							</p>

							<h4>Vårdnadshavare 1</h4>
							<FieldsCollection
								fieldsPerRow={2}
								fields={[
									{
										render: (
											<EditableField
												name="caretaker_name_1"
												label="Namn"
												value={state.fields.caretaker_name_1}
												valid={state.fields.caretaker_name_1.length > 0}
												className={state.fields.caretaker_name_1.trim().length === 0 ? "empty" : ""}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									},
									{
										render: (
											<EditableField
												name="caretaker_ssn_1"
												label="Personnummer"
												value={state.fields.caretaker_ssn_1}
												className={state.fields.caretaker_ssn_1.trim().length === 0 ? "empty" : ""}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												valid={Validator.validateSsn(state.fields.caretaker_ssn_1)}
												required
											/>
										)
									}
								]}
							/>

							<h4>Vårdnadshavare 2</h4>
							<FieldsCollection
								fieldsPerRow={2}
								fields={[
									{
										render: (
											<EditableField
												name="caretaker_name_2"
												label="Namn"
												value={state.fields.caretaker_name_2}
												valid={state.fields.caretaker_name_2.length > 0}
												className={state.fields.caretaker_name_2.trim().length === 0 ? "empty" : ""}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												required
											/>
										),
									},
									{
										render: (
											<EditableField
												name="caretaker_ssn_2"
												label="Personnummer"
												value={state.fields.caretaker_ssn_2}
												className={state.fields.caretaker_ssn_2.trim().length === 0 ? "empty" : ""}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												valid={Validator.validateSsn(state.fields.caretaker_ssn_2)}
												required
											/>
										)
									}
								]}
							/>
						</div>
					)}

					<h3 style={{
						width: "100%",
						margin: "40px 0 8px 0",
						borderTop: "1px solid #EEE",
						paddingTop: "40px"
					}}>
						Skattehemvist
					</h3>

					<FieldsCollection
						fields={[
							{
								render: (
									<OptionsList
										type="radio"
										orientation="horizontal"
										name="radio_customer_kyc_taxdom"
										description="Har du skattehemvist i Sverige?"
										options={state.fields.radio_customer_kyc_taxdom.options}
										onChange={this._onOptionsListChange}
									/>
								),
							},
						]}
					/>

					{state.fields.radio_customer_kyc_taxdom.options[0].checked && (
						<div style={{
							display: "block",
							width: "560px",
							margin: "20px 0 0 0",
							background: "#FFEAEA",
							padding: "15px"
						}}>
							Det är tyvärr inte möjligt att öppna ett ISK om du inte har skattehemvist i Sverige.
						</div>
					)}


					<h3 style={{width: "100%", margin: "40px 0 0 0"}}>
						PEP - Politiskt exponerad person
					</h3>

					<div className="description">
						<p>
							PEP – Person i politiskt utsatt ställning – Person som har eller tidigare har haft en hög
							politisk post, en hög statlig befattning eller funktion i ledningen i en internationell
							organisation, eller har en nära familjemedlem eller medarbetare med ovanstående befattning.
						</p>
					</div>

					<FieldsCollection
						fields={[
							{
								render: (
									<OptionsList
										type="radio"
										orientation="horizontal"
										name="radio_customer_pep"
										description="Är du själv, eller har du varit, en politiskt exponerad person/politiskt utsatt person?"
										options={state.fields.radio_customer_pep.options}
										onChange={this._onOptionsListChange}
									/>
								),
							},
						]}
					/>

					{state.fields.radio_customer_pep.options[1].checked && (
						<div style={{margin: "10px 0 0 0", display: "block", width: "100%"}}>
							<QuestionField
								Component={Select}
								description="Vilken typ av PEP är du?"
								label="Välj"
								name={state.fields.radio_customer_pep_details.name}
								selectedIndex={state.fields.radio_customer_pep_details.index}
								options={state.fields.radio_customer_pep_details.options}
								onChange={this._onSelectPepChange}
							/>

						</div>
					)}

					{
						state.fields.radio_customer_pep.options[1].checked &&
						state.fields.radio_customer_pep_details.index !== -1 && (
							<input type="hidden" name="pep_info"
								   value={state.fields.radio_customer_pep_details.options[state.fields.radio_customer_pep_details.index].label}/>
						)
					}

					<FieldsCollection
						fields={[
							{
								render: (
									<OptionsList
										type="radio"
										orientation="horizontal"
										style={{margin: "10px 0 0 0"}}
										name="radio_customer_pep_past"
										description="Har du någon närstående som är, eller har varit, en politiskt exponerad person/politiskt utsatt person?"
										options={state.fields.radio_customer_pep_past.options}
										onChange={this._onOptionsListChange}
									/>
								),
							},
						]}
					/>

					{state.fields.radio_customer_pep_past.options[1].checked && (
						<div style={{margin: "10px 0 0 0", display: "block", width: "100%"}}>
							<QuestionField
								Component={Select}
								description="Vilken typ av PEP är din närstående?"
								label="Välj"
								name={state.fields.radio_customer_pep_past_details.name}
								selectedIndex={state.fields.radio_customer_pep_past_details.index}
								options={state.fields.radio_customer_pep_past_details.options}
								onChange={this._onSelectPepChange}
							/>
						</div>
					)}

					{
						state.fields.radio_customer_pep_past.options[1].checked &&
						state.fields.radio_customer_pep_past_details.index !== -1 && (
							<input type="hidden" name="pep_info_relative"
								   value={state.fields.radio_customer_pep_past_details.options[state.fields.radio_customer_pep_past_details.index].label}/>
						)
					}

					<h3 style={{width: "100%", margin: "40px 0 0 0"}}>
						FATCA
					</h3>

					<div className="description">
						<p>
							U.S. person – Person som har ett Amerikanskt medborgarskap eller på annat sätt är
							skatteskyldig i USA.
						</p>
					</div>

					<FieldsCollection
						fields={[
							{
								render: (
									<OptionsList
										type="radio"
										orientation="horizontal"
										name="radio_customer_us_person"
										description="Är du en så kallad U.S. person?"
										options={state.fields.radio_customer_us_person.options}
										onChange={this._onOptionsListChange}
									/>
								),
							},
						]}
					/>

					{state.fields.radio_customer_us_person.options[1].checked && (
						<div style={{
							display: "block",
							width: "560px",
							margin: "20px 0 0 0",
							background: "#FFEAEA",
							padding: "15px"
						}}>
							Det är tyvärr inte möjligt att öppna ett ISK om du är U.S. Person.
						</div>
					)}
				</div>

				<input type="hidden" name="checkbox_account_isk" value="yes"/>
				<input type="hidden" name="date" value={date} />

			</div>
		);
	}

	// Internal methods
	_fieldValidator = (name, value) => {
		if (name === "customer_social_security_number") {
			return Validator.validateSsn(value, true);
		}
		else if (name === "customer_email") {
			return Validator.validateEmail(value);
		}
		else {
			return value.length > 0;
		}
	};

	_onFieldChange = (name, value) => {
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onUnlock = () => {
		this.setState({fieldsLocked: false});
	};

	_fetchCustomerDetails = async () => {
		this.setState({busy: true}, async () => {
			let lookupData = null;

			let isCompany = !Validator.validateSsn(this.state.fields.customer_social_security_number, true) &&
				Validator.validateOrgNum(this.state.fields.customer_social_security_number, true);

			try {
				lookupData = await api("/lookup", {
					"type": (isCompany ? "company" : "person"),
					"value": this.state.fields.customer_social_security_number
				});
			}
			catch (e) {
				console.log(e);
			}

			if (lookupData == null) {
				// Show error
				this.setState({busy: false, fieldsLocked: false});
				return;
			}

			const fieldsData = this.state.fields;
			fieldsData.customer_address_street = lookupData.street;
			fieldsData.customer_address_zipcode = lookupData.zipCode;
			fieldsData.customer_address_city = lookupData.city;
			fieldsData.customer_phonenumber_cell = lookupData.phoneNumber && lookupData.phoneNumber !== '-' ? lookupData.phoneNumber : "";

			if (isCompany) {
				fieldsData.customer_firstname = lookupData.companyName;
				fieldsData.customer_lastname = "";

				fieldsData.company_firma = lookupData.registeredFirmSignee;
				fieldsData.company_ceo = lookupData.ceo;
				fieldsData.company_board = lookupData.board;
			}
			else {
				fieldsData.customer_firstname = lookupData.firstName;
				fieldsData.customer_lastname = lookupData.lastName;
				fieldsData.company_firma = "";
				fieldsData.company_ceo = null;
				fieldsData.company_board = [];
			}

			this.setState({busy: false, fieldsLocked: false, fields: fieldsData});
		});

	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_onRepsUpdated = (reps) => {
		this.setState({fields: {...this.state.fields, reps: reps}});
	};

	_onSelectPepChange = (option, index) => {
		this.setState({
			fields: {
				...this.state.fields,
				[option.name]: {
					...this.state.fields[option.name],
					index: index,
				},
				[option.name + "_value"]: option.label
			},
		}, () => fulfillmentCheck(this.props, this.state));
	};

	_onOptionsListChange = ({type, name}, option, index) => {
		const newOptions = [...this.state.fields[name].options].map((o, i) => {
			if (i === index) {
				return option;
			}
			else {
				return {...o, checked: type === "radio" ? false : o.checked};
			}
		});

		this.setState({
			fields: {
				...this.state.fields,
				[name]: {
					options: newOptions,
				},
			},
		}, () => fulfillmentCheck(this.props, this.state));
	};
}

// PRIVATE FUNCTIONS
function _getFields() {
	return [
		"customer_firstname",
		"customer_lastname",
		"customer_social_security_number",
		"customer_address_street",
		"customer_address_zipcode",
		"customer_address_city",
		"customer_email",
		"radio_customer_kyc_taxdom",
		"customer_phonenumber_home",
		"customer_phonenumber_cell",
		"caretaker_name_1",
		"caretaker_name_2",
		"caretaker_ssn_1",
		"caretaker_ssn_2",
	];
}

function _getInitialState() {
	if (this.props.prevState) return {...this.props.prevState};

	return {
		busy: false,
		fieldsLocked: true,
		validFields: {},
		fields: {
			"customer_firstname": "",
			"customer_lastname": "",
			"customer_social_security_number": "",
			"customer_address_street": "",
			"customer_address_zipcode": "",
			"customer_address_city": "",
			"customer_email": "",

			"customer_phonenumber_home": "",
			"customer_phonenumber_cell": "",

			"caretaker_name_1": "",
			"caretaker_name_2": "",
			"caretaker_ssn_1": "",
			"caretaker_ssn_2": "",

			"radio_customer_pep": {
				options: [
					{label: "Nej", value: "no", checked: false},
					{label: "Ja", value: "yes", checked: false}
				]
			},

			"radio_customer_pep_details": {
				index: -1,
				name: "radio_customer_pep_details",
				options: [
					{
						label: "Statschefen",
						name: "radio_customer_pep_details"
					},
					{
						label: "Riksdagsledamöter",
						name: "radio_customer_pep_details"
					},
					{
						label: "Statsministern och övriga ministrar i regeringskansliet",
						name: "radio_customer_pep_details"
					},
					{
						label: "Domare i Högsta domstolen och Högsta förvaltningsdomstolen",
						name: "radio_customer_pep_details"
					},
					{
						label: "Riksrevisorerna",
						name: "radio_customer_pep_details"
					},
					{
						label: "Riksbankens direktion",
						name: "radio_customer_pep_details"
					},
					{
						label: "Ambassadörer",
						name: "radio_customer_pep_details"
					},
					{
						label: "Höga officerare (general, generallöjtnant, generalmajor, amiral, viceamiral och konteramiral)",
						name: "radio_customer_pep_details"
					},
					{
						label: "Vd eller styrelseledamot i statsägda företag",
						name: "radio_customer_pep_details"
					},
					{
						label: "Person i ledningen i en internationell organisation (styrelseledamöter, generalsekreterare med flera)",
						name: "radio_customer_pep_details"
					},
					{
						label: "Ledamot i politiskt partis styrelse",
						name: "radio_customer_pep_details"
					}
				]
			},
			"pep_info": "",

			"radio_customer_pep_past": {
				options: [
					{label: "Nej", value: "no", checked: false},
					{label: "Ja", value: "yes", checked: false}
				]
			},

			"radio_customer_pep_past_details": {
				index: -1,
				name: "radio_customer_pep_past_details",
				options: [
					{
						label: "Statschefen",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Riksdagsledamöter",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Statsministern och övriga ministrar i regeringskansliet",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Domare i Högsta domstolen och Högsta förvaltningsdomstolen",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Riksrevisorerna",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Riksbankens direktion",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Ambassadörer",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Höga officerare (general, generallöjtnant, generalmajor, amiral, viceamiral och konteramiral)",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Vd eller styrelseledamot i statsägda företag",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Person i ledningen i en internationell organisation (styrelseledamöter, generalsekreterare med flera)",
						name: "radio_customer_pep_past_details"
					},
					{
						label: "Ledamot i politiskt partis styrelse",
						name: "radio_customer_pep_past_details"
					}
				]
			},
			"pep_info_relative": "",

			"radio_customer_us_person": {
				options: [
					{label: "Nej", value: "no", checked: false},
					{label: "Ja (det är ej möjligt att öppna konto)", value: "yes", checked: false}
				]
			},

			"radio_customer_kyc_taxdom": {
				options: [
					{label: "Nej", value: "no", checked: false},
					{label: "Ja", value: "yes", checked: false}
				]
			},

			"company_firma": "",
			"company_ceo": null,
			"company_board": [],
			"reps": [],
		},
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/CustomerInformation/fair-gds-oppna/CustomerInformation.js